import { boundingBox } from '../../common/pdfReport';
import { CONTROL_POINTS } from './geoJsonExport';

/**
 * Describes a MEPBAY analysis as a document.
 * MEPBAY fits a parabolic bay shape to each bay the user marks out, so the result is not one number
 * but one set of coefficients per bay. The report is organised the same way: a row per bay with its
 * fit, and the control points it was fitted from, because a curve quoted without the three points
 * that produced it is a shape nobody can check.
 */

/**
 * @constant REPORT_MODULE
 * @description Module key, used to name the file and to register the export.
 * @memberof module:Mepbay
 */
export const REPORT_MODULE = 'mepbay';

/**
 * @function analysedBays
 * @description The layers that carry a fitted bay, in the order they were added. The area of
 * interest is a layer too and is skipped, as it is in the GeoJSON export.
 * @param {Object} [layers] - MEPBAY's layers, keyed by id.
 * @memberof module:Mepbay
 * @returns {Array<Object>}
 */
export const analysedBays = (layers) =>
  Object.values(layers || {}).filter((layer) => !layer?.isROI && layer?.parabolicResults);

/**
 * @function bayTable
 * @description One row per bay: the angle the fit turned on and the four coefficients that define
 * the curve.
 * @param {Array<Object>} bays - From {@link analysedBays}.
 * @param {Object} text - `{ columns: { bay, beta, cosBeta, c0, c1, c2, r0 } }`, already translated.
 * @memberof module:Mepbay
 * @returns {Object} A table for {@link renderReport}.
 */
export const bayTable = (bays, text) => {
  const fixed = (value, places = 3) =>
    value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
      ? null
      : Number(value).toFixed(places);

  return {
    columns: [
      text.columns.bay,
      text.columns.beta,
      text.columns.cosBeta,
      text.columns.c0,
      text.columns.c1,
      text.columns.c2,
      text.columns.r0,
    ],
    rows: (bays || []).map((bay, index) => {
      const results = bay.parabolicResults || {};
      return [
        bay.name ?? bay.id ?? index + 1,
        fixed(results.beta, 2),
        fixed(results.cosBetaConf),
        fixed(results.C0),
        fixed(results.C1),
        fixed(results.C2),
        fixed(results.R0, 2),
      ];
    }),
  };
};

/**
 * @function controlPointTable
 * @description The three points each bay was fitted from, as coordinates.
 * @param {Array<Object>} bays - From {@link analysedBays}.
 * The three are listed in the order {@link CONTROL_POINTS} fixes, which is the order the GeoJSON
 * export writes them in and the order the user marks them on the map - sharing that list is what
 * keeps a bay's points from being named one way in one file and another way in the other.
 * @param {Object} text - `{ columns: { bay, point, longitude, latitude }, names }`, already
 *  translated, `names` keyed by each control point's key.
 * @memberof module:Mepbay
 * @returns {Object} A table for {@link renderReport}.
 */
export const controlPointTable = (bays, text) => ({
  columns: [text.columns.bay, text.columns.point, text.columns.longitude, text.columns.latitude],
  align: ['left', 'left', 'right', 'right'],
  rows: (bays || []).flatMap((bay, index) =>
    CONTROL_POINTS.map(({ key }) => ({ key, point: bay.controlPoints?.[key] }))
      .filter(({ point }) => Number.isFinite(Number(point?.lng)) && Number.isFinite(Number(point?.lat)))
      .map(({ key, point }) => [
        bay.name ?? bay.id ?? index + 1,
        text.names?.[key] ?? key,
        Number(point.lng).toFixed(5),
        Number(point.lat).toFixed(5),
      ]),
  ),
});

/**
 * @function buildMepBayReport
 * @description The whole document, as data.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.coordinates] - Area of interest ring.
 * @param {Object} [params.layers] - MEPBAY's layers.
 * @param {String} [params.kmlFileName] - The uploaded area file.
 * @param {String} [params.imageDate] - The image the bays were marked on.
 * @param {Array<Object>} [params.images] - Already loaded by {@link loadImageForReport}.
 * @param {Object} params.text - Every piece of wording, already translated.
 * **Called by:
 * @see {ProcessingPage} - MEPBAY's result page.
 * @memberof module:Mepbay
 * @returns {Object} A document description for {@link renderReport}.
 */
export const buildMepBayReport = ({ coordinates, layers, kmlFileName, imageDate, images, text }) => {
  const bays = analysedBays(layers);

  return {
    title: text.title,
    subtitle: text.subtitle,
    footer: text.footer,
    missingImageNote: text.missingImage,
    sections: [
      {
        heading: text.areaHeading,
        rows: [
          [text.areaFile, kmlFileName],
          [text.areaExtent, boundingBox(coordinates)],
          [text.imageDate, imageDate],
          [text.bayCount, bays.length || null],
        ],
      },
      {
        heading: text.fitHeading,
        table: bayTable(bays, text.bays),
        note: bays.length ? text.fitNote : null,
      },
      {
        heading: text.controlHeading,
        table: controlPointTable(bays, text.controlPoints),
        note: bays.length ? text.controlNote : null,
      },
      { heading: text.mapsHeading, images: images || [] },
    ],
  };
};
